/**
 * HandwritingTab — how this student's handwriting should be READ.
 *
 * Every reader in this system works from an image, so a habit of one hand costs
 * the same marks repeatedly: one student's `=` was transcribed as `2` by both
 * the splitter and `extract_visuals`, turning correct working into wrong answers
 * across a whole paper. Generic glyph advice in the prompt cannot fix that,
 * because the confusion belongs to one hand.
 *
 * Deliberately NOT in student settings. The evidence for a quirk lives in the
 * extraction output, which only this console shows — and the text is handed to
 * the reader whose independence the reader-consistency signal depends on, so the
 * person it describes is the wrong author for it.
 *
 * Writes `User.handwritingNotes` through PATCH /console/users/{id}, which
 * applies the same claim-shaped guard this form warns about.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@shared-ui/components';
import './HandwritingTab.css';
import { userApi } from '../../../api/userApi';

interface HandwritingTabProps {
  userId: string;
  /** Current value, from the already-loaded user record. */
  value?: string;
}

const MAX_LEN = 1000;

export function HandwritingTab({ userId, value }: HandwritingTabProps) {
  const qc = useQueryClient();
  const [text, setText] = useState(value ?? '');
  const [saved, setSaved] = useState(false);
  // What the server holds, as far as this tab knows: the prop until a save,
  // then what was saved. "Dirty" is measured against this, not the prop alone
  // — the prop only changes when the user query refetches, and a save that
  // was accepted must read as saved before then, not stay "unsaved" until a
  // round-trip that (if the detail response ever omits the field) never
  // catches up.
  const [persisted, setPersisted] = useState(value ?? '');

  // Re-seed when the user record arrives or changes underneath.
  useEffect(() => { setText(value ?? ''); setPersisted(value ?? ''); setSaved(false); }, [value, userId]);

  const save = useMutation({
    mutationFn: () => {
      const next = text.trim();
      return userApi.updateUser(userId, { handwritingNotes: next || null }).then(() => next);
    },
    onSuccess: (next) => {
      setPersisted(next);
      setSaved(true);
      // Must match UserDetail's query key — the tab remounts from that cache
      // on every tab switch, so a missed invalidation shows the old value.
      qc.invalidateQueries({ queryKey: ['user-detail', userId] });
    },
  });

  const dirty = (text.trim() || '') !== persisted;

  return (
    <div className="features-section handwriting-tab">
      <h2>How this student forms characters</h2>
      <p className="logging-subtitle">
        Given to the extractor and the visual analyser before they read a page. Write
        what a stroke <strong>is</strong> — which characters are easily confused in this
        hand, and what they actually are.
      </p>
      {/* The constraint is the whole design, so it is stated where it is typed
          rather than only enforced on submit. */}
      <p className="logging-subtitle">
        Do not describe the student. Nothing about their ability, their usual mistakes,
        or what their answers tend to be: a reader told what to expect stops reading,
        and this text goes to the pass whose independence the misread-detection depends
        on. The server refuses notes written that way.
      </p>
      <textarea
        className="handwriting-notes"
        value={text}
        onChange={e => { setText(e.target.value); setSaved(false); }}
        maxLength={MAX_LEN}
        rows={5}
        placeholder="e.g. Writes = with a curled top bar; both readers have transcribed it as 2."
      />
      <div className="handwriting-meta">
        {text.length}/{MAX_LEN} · applies to the next analysis; nothing already scored changes.
      </div>
      <div className="handwriting-actions">
        <Button onClick={() => save.mutate()} disabled={save.isPending || !dirty} loading={save.isPending}>
          Save
        </Button>
        {text && (
          <Button variant="outline" onClick={() => { setText(''); save.mutate(); }} disabled={save.isPending}>
            Clear
          </Button>
        )}
        {saved && !dirty && <span className="handwriting-saved">Saved.</span>}
      </div>
      {save.isError && (
        <div className="error-message handwriting-error">
          {save.error instanceof Error ? save.error.message : 'Save failed'}
        </div>
      )}
    </div>
  );
}
